import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@/auth/UserContext';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { BodyPortal } from '@/components/BodyPortal';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { PrintPreviewCard } from '@/components/DiaryPrintModal/PrintPreviewCard';
import { EmptyState } from '@/components/EmptyState';
import { Close } from '@/components/Icons/Close';
import { Download } from '@/components/Icons/Download';
import { Modal } from '@/components/Modal';
import { Snackbar } from '@/components/Snackbar';
import { ButtonVariant, SnackbarVariant } from '@/types/components';
import type { DiaryEntry, DiaryPrintOptions } from '@/types/diary';
import { formatDate, formatDatePeriod } from '@/utils/date';
import { downloadDiaryPdf } from '@/utils/diaryPdf';
import { getFullName } from '@/utils/text';

interface Props {
  entries: DiaryEntry[];
  onClose: () => void;
}

export const DiaryPrintModal: React.FC<Props> = ({ entries, onClose }) => {
  const { t } = useTranslation();
  const { currentProfile } = useUserContext();
  const [options, setOptions] = useState<DiaryPrintOptions>({
    isTopicColumnVisible: true,
    isHomeworkColumnVisible: true,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    message: '',
    variant: SnackbarVariant.Success,
  });
  const title = t('diary.print.title');
  const firstDate = entries.at(0)?.date;
  const lastDate = entries.at(-1)?.date;
  const dateRange =
    firstDate && lastDate ? formatDatePeriod(firstDate, lastDate) : '';
  const fileName =
    firstDate && lastDate
      ? `timetable-${formatDate(firstDate)}-${formatDate(lastDate)}.pdf`
      : 'timetable.pdf';
  const studentName = getFullName(currentProfile?.user);

  const showSnackbar = (message: string, variant: SnackbarVariant) => {
    setSnackbar({ message, variant });
    setIsSnackbarOpen(true);
  };

  const handleDownload = async () => {
    setIsExporting(true);

    try {
      await downloadDiaryPdf({
        fileName,
        studentName,
        entries,
        options,
        labels: {
          subject: t('diary.table.subject'),
          topic: t('diary.table.topic'),
          homework: t('diary.table.homework'),
          emptyLesson: t('diary.emptyLesson'),
          emptyDescription: t('diary.emptyDescription'),
          deletedHomeTask: t('diary.homeTask.deleted'),
          teacherMessage: t('diary.notes.teacherMessage'),
          behaviourEntry: t('diary.notes.behaviourEntry'),
          prepared: t('diary.print.prepared', {
            date: formatDate(new Date().toISOString()),
          }),
        },
      });
      showSnackbar(t('mail.detail.documentSaved'), SnackbarVariant.Success);
    } catch {
      showSnackbar(t('global.errorOccurred'), SnackbarVariant.Error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Modal
        isOpen
        label={title}
        className="md:h-[85svh] md:w-[min(1000px,calc(100vw-40px))]"
        onClose={onClose}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 px-5 pt-5 pb-6">
          <div className="flex min-w-0 flex-col gap-1">
            <h2 className="text-headline-20 truncate text-foreground">
              {title}
            </h2>
            <p className="text-body-14 text-subtle-foreground">{dateRange}</p>
          </div>
          <button
            type="button"
            aria-label={t('global.close')}
            onClick={onClose}
            className="flex size-4 shrink-0 cursor-pointer items-center justify-center text-foreground"
          >
            <Close className="size-4" />
          </button>
        </header>

        <div className="flex shrink-0 items-end justify-between gap-4 px-5 pb-6">
          <div className="flex flex-col gap-4">
            <p className="text-callout-16 text-foreground">
              {t('diary.print.options')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Checkbox
                checked={options.isTopicColumnVisible}
                label={t('diary.print.showTopics')}
                onChange={(isTopicColumnVisible) =>
                  setOptions({ ...options, isTopicColumnVisible })
                }
              />
              <Checkbox
                checked={options.isHomeworkColumnVisible}
                label={t('diary.print.showHomework')}
                onChange={(isHomeworkColumnVisible) =>
                  setOptions({ ...options, isHomeworkColumnVisible })
                }
              />
            </div>
          </div>

          <Button
            variant={ButtonVariant.Primary}
            className="text-subhead-14 font-semibold h-9"
            isLoading={isExporting}
            onClick={handleDownload}
          >
            <Download className="size-4" />
            {t('diary.print.download')}
          </Button>
        </div>

        <p className="text-callout-16 shrink-0 px-5 pb-4 text-foreground">
          {t('diary.print.preview')}
        </p>

        <AutoHideScrollArea
          hasTopShadow
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex flex-col gap-6 px-5 pb-5">
            {!entries.length && <EmptyState label={t('diary.emptyDay')} />}
            {entries.map((entry) => (
              <PrintPreviewCard
                key={entry.date}
                entry={entry}
                options={options}
              />
            ))}
          </div>
        </AutoHideScrollArea>
      </Modal>

      <BodyPortal>
        <Snackbar
          message={snackbar.message}
          variant={snackbar.variant}
          isOpen={isSnackbarOpen}
          onDismiss={() => setIsSnackbarOpen(false)}
        />
      </BodyPortal>
    </>
  );
};
